import type { AgentId } from '@developerz/domain';
import type { JSX } from 'solid-js';
import { t } from '../i18n';
import { useAgentQueue } from '../lib/agent-queue';
import { AgentQueueList } from './AgentQueueList';
import { EmptyState } from './EmptyState';
import { LoadMore } from './LoadMore';
import { QueryBoundary } from './QueryBoundary';
import { Skeleton } from './Skeleton';

// The body of an agent card's queue drawer (#6210). Container — reads one agent's
// queue (`lib/agent-queue.ts`) while `open`, and hands the rows to `AgentQueueList`.
// `ActionDrawer` mounts its body only while visible, so a card nobody opened reads
// nothing; the read is ALSO gated on `open`, so the drawer's closing slide (it stays
// mounted for the exit animation) never starts one.

/**
 * Reads one agent's queue while `open` and renders it, with loading, empty and Load more
 * states. `handOverModel` is the agent's org pin, for the escalation queue's Stuck PRs group.
 */
export function AgentQueuePanel(props: {
  readonly agent: AgentId;
  readonly agentName: string;
  readonly open: boolean;
  readonly token: string | null;
  readonly handOverModel: string | null;
}): JSX.Element {
  const list = useAgentQueue(props.agent, () => props.open, props.token);
  return (
    <div class="agent-queue-panel">
      <p class="agent-queue-panel__intro">{t('agents.queue.intro')}</p>
      <QueryBoundary
        query={list.view}
        loading={<Skeleton height="6rem" />}
        empty={<EmptyState message={t('agents.queue.empty', { agent: props.agentName })} />}
        isEmpty={(rows) => rows.length === 0}
      >
        {(rows) => (
          <>
            <AgentQueueList rows={rows} handOverModel={props.handOverModel} />
            <LoadMore list={list} />
          </>
        )}
      </QueryBoundary>
    </div>
  );
}
